import React, { useState } from "react";
import { PenLine, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import YousignWebhookSection from "./YousignWebhookSection";

export default function YousignCard({ data, onChange }) {
  const [key, setKey] = useState("");
  const [env, setEnv] = useState(data?.env || "sandbox");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); } finally { setBusy(false); onChange(); } };
  const save = () => run(async () => {
    await api.post("/connections/yousign/save", { api_key: key, env });
    setKey("");
    const r = await api.post("/connections/yousign/test");
    setResult(r.data);
  });
  const test = () => run(async () => setResult((await api.post("/connections/yousign/test")).data));
  const remove = () => window.confirm("Supprimer la clé Yousign ?") && run(() => api.post("/connections/yousign/disconnect"));
  return (
    <div className="bg-white border border-norea-border rounded-lg p-6" data-testid="yousign-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center text-norea-primary"><PenLine className="w-5 h-5" /></div>
        <div>
          <h3 className="font-heading text-xl font-medium">Yousign</h3>
          <div className="mt-1 text-xs" data-testid="yousign-card-status">
            {data?.configured
              ? <span className="badge-status badge-complet"><CheckCircle2 className="w-3 h-3" /> Clé enregistrée · {data.env === "sandbox" ? "mode test (sandbox)" : "production"}</span>
              : <span className="text-norea-textMuted">Non configuré</span>}
          </div>
        </div>
      </div>
      <p className="text-sm text-norea-textMuted mb-4">Signature électronique des accords, protocoles, attestations et renonciations. Le PDF signé est rangé tout seul dans l'onglet Dossier.</p>
      {!data?.configured && (
        <div className="mb-4 p-3 bg-norea-primary/5 border border-norea-primary/20 rounded-md text-xs">
          <div className="font-semibold mb-1">Où trouver votre clé API ?</div>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li>Connectez-vous à <a href="https://app.yousign.com" target="_blank" rel="noreferrer" className="text-norea-primary hover:underline">app.yousign.com</a></li>
            <li>Menu <b>Développeurs → Clés API</b>, créez une clé <b>Sandbox</b> pour commencer</li>
            <li>Collez-la ci-dessous, choisissez « Sandbox » et enregistrez</li>
          </ol>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-norea-textMuted uppercase tracking-wider mb-1">Clé API Yousign</span>
          <div className="relative">
            <input type={show ? "text" : "password"} value={key} onChange={(e) => setKey(e.target.value)} placeholder={data?.api_key_masked || "Collez votre clé Yousign ici"}
              className="w-full px-3 py-2 pr-10 border border-norea-border rounded-md text-sm outline-none focus:border-norea-primary font-mono" data-testid="yousign-key-input" />
            <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-norea-textMuted" data-testid="yousign-toggle-key">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
          </div>
          {data?.api_key_masked && <span className="text-[11px] text-norea-textMuted">Clé actuelle : <span className="font-mono">{data.api_key_masked}</span> · laissez vide pour la conserver.</span>}
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-norea-textMuted uppercase tracking-wider mb-1">Environnement</span>
          <select value={env} onChange={(e) => setEnv(e.target.value)} className="w-full px-3 py-2 border border-norea-border rounded-md text-sm" data-testid="yousign-env-select">
            <option value="sandbox">Sandbox (test)</option>
            <option value="production">Production</option>
          </select>
        </label>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button onClick={save} disabled={busy || (!key.trim() && !data?.api_key_masked)} className="btn-primary disabled:opacity-50" data-testid="yousign-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer et tester"}</button>
        <button onClick={test} disabled={busy || !data?.configured} className="btn-secondary disabled:opacity-50" data-testid="yousign-test-btn">Tester la connexion</button>
        {data?.api_key_masked && <button onClick={remove} disabled={busy} className="text-xs text-norea-textMuted hover:text-status-erreurText ml-auto" data-testid="yousign-disconnect-btn">Supprimer la clé</button>}
      </div>
      {result && (
        <div className={`mt-3 px-3 py-2 rounded-md text-sm flex items-center gap-1.5 ${result.ok ? "bg-status-completBg text-status-completText" : "bg-status-erreurBg text-status-erreurText"}`} data-testid="yousign-test-result">
          {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}{result.ok ? result.message : result.error}
        </div>
      )}
      <YousignWebhookSection data={data} onChange={onChange} />
    </div>
  );
}
